import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import AdminLayout from "@/components/layout-admin";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import {
  Mail,
  CreditCard,
  Lock,
  Webhook,
  Calendar,
  CheckCircle2,
  XCircle,
  Send,
  ExternalLink,
  Settings2,
  Zap,
  Globe,
  MessageSquare,
  Shield,
  RefreshCw,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { apiRequest } from "@/lib/queryClient";

interface IntegrationCardProps {
  icon: typeof Mail;
  title: string;
  description: string;
  configured: boolean;
  provider?: string | null;
  onConfigure?: () => void;
  children?: React.ReactNode;
  badge?: string;
}

function IntegrationCard({ icon: Icon, title, description, configured, provider, onConfigure, children, badge }: IntegrationCardProps) {
  return (
    <Card className="relative overflow-hidden" data-testid={`integration-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardContent className="p-5">
        <div className="flex items-start gap-4">
          <div className={`p-3 rounded-xl shrink-0 ${configured ? 'bg-green-50 dark:bg-green-900/20' : 'bg-muted'}`}>
            <Icon className={`w-6 h-6 ${configured ? 'text-green-600' : 'text-muted-foreground'}`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-semibold text-sm">{title}</h3>
              {badge && (
                <Badge variant="secondary" className="text-[10px]">{badge}</Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground mb-3">{description}</p>
            <div className="flex items-center gap-2 flex-wrap">
              {configured ? (
                <Badge variant="default" className="gap-1 bg-green-600 hover:bg-green-700">
                  <CheckCircle2 className="w-3 h-3" />
                  Connected
                </Badge>
              ) : (
                <Badge variant="outline" className="gap-1 text-muted-foreground">
                  <XCircle className="w-3 h-3" />
                  Not configured
                </Badge>
              )}
              {provider && (
                <Badge variant="secondary" className="text-xs">{provider}</Badge>
              )}
            </div>
            {children && <div className="mt-3">{children}</div>}
          </div>
          {onConfigure && (
            <Button variant="outline" size="sm" onClick={onConfigure} className="shrink-0" data-testid={`button-configure-${title.toLowerCase().replace(/\s+/g, '-')}`}>
              <Settings2 className="w-4 h-4 mr-1" />
              {configured ? 'Settings' : 'Set Up'}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function AdminIntegrations() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [syncTime1, setSyncTime1] = useState("08:00");
  const [syncTime2, setSyncTime2] = useState("20:00");
  const [syncScheduleSaved, setSyncScheduleSaved] = useState(false);

  const { data: emailStatus } = useQuery<{ configured: boolean; provider: string | null; from: string }>({
    queryKey: ['/api/admin/email/status'],
  });

  const { data: orgSettings } = useQuery<any>({
    queryKey: ['/api/admin/org-settings'],
  });

  useEffect(() => {
    if (orgSettings?.icalSyncTimes) {
      const times: string[] = orgSettings.icalSyncTimes;
      if (times[0]) setSyncTime1(times[0]);
      if (times[1]) setSyncTime2(times[1]);
    }
  }, [orgSettings]);

  const [syncAllResult, setSyncAllResult] = useState<{ synced: number; created: number; updated: number; cancelled: number; errors: string[] } | null>(null);
  const [backfillResult, setBackfillResult] = useState<{ created: number; skipped: number; errors: number } | null>(null);

  const syncAllNow = useMutation({
    mutationFn: async () => {
      const res = await apiRequest('POST', '/api/admin/ical-sync-all', {});
      return res as unknown as { synced: number; created: number; updated: number; cancelled: number; errors: string[] };
    },
    onSuccess: (data) => {
      setSyncAllResult(data);
      toast({ title: `Synced ${data.synced} apartments`, description: `+${data.created} new · ~${data.updated} updated · ${data.cancelled} cancelled${data.errors.length ? ` · ${data.errors.length} errors` : ''}` });
    },
    onError: () => toast({ title: "Sync failed", variant: "destructive" }),
  });

  const backfillJobs = useMutation({
    mutationFn: async () => {
      const res = await apiRequest('POST', '/api/admin/backfill-cleaning-jobs', {});
      return res as unknown as { created: number; skipped: number; errors: number };
    },
    onSuccess: (data) => {
      setBackfillResult(data);
      toast({ title: `Backfill complete`, description: `${data.created} cleaning jobs created · ${data.skipped} already had jobs` });
    },
    onError: () => toast({ title: "Backfill failed", variant: "destructive" }),
  });

  const saveSyncTimes = useMutation({
    mutationFn: async () => {
      const times = [syncTime1, syncTime2].filter(Boolean);
      return await apiRequest('PATCH', '/api/admin/org-settings', { icalSyncTimes: times });
    },
    onSuccess: () => {
      setSyncScheduleSaved(true);
      setTimeout(() => setSyncScheduleSaved(false), 2000);
      toast({ title: "Sync schedule saved" });
    },
    onError: () => {
      toast({ title: "Failed to save schedule", variant: "destructive" });
    },
  });


  const sendTestEmail = useMutation({
    mutationFn: async (to: string) => {
      const res = await fetch('/api/admin/email/test', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ to }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to send test email');
      return data;
    },
    onSuccess: () => {
      toast({ title: "Test email sent", description: "Check your inbox for the test email." });
      setEmailDialogOpen(false);
      setTestEmail("");
    },
    onError: (err: Error) => {
      toast({ title: "Failed to send", description: err.message, variant: "destructive" });
    },
  });

  const integrations = [
    {
      category: "Communication",
      items: [
        {
          icon: Mail,
          title: "Email (Resend)",
          description: "Send transactional emails for booking confirmations, check-in instructions, payment receipts, and automated workflows.",
          configured: !!emailStatus?.configured,
          provider: emailStatus?.provider,
          badge: "Transactional",
          onConfigure: () => setEmailDialogOpen(true),
        },
        {
          icon: MessageSquare,
          title: "SMS (Twilio)",
          description: "Send SMS notifications to guests and team members for check-ins, cleaning assignments, and alerts.",
          configured: false,
          badge: "Coming Soon",
        },
        {
          icon: Globe,
          title: "WhatsApp",
          description: "Send and receive WhatsApp messages through the Guest CRM for real-time guest communication.",
          configured: false,
          badge: "Coming Soon",
        },
      ],
    },
    {
      category: "Payments",
      items: [
        {
          icon: CreditCard,
          title: "Stripe",
          description: "Process payments, collect deposits, and manage subscriptions for your properties.",
          configured: false,
          badge: "Coming Soon",
        },
      ],
    },
    {
      category: "Channel Management",
      items: [
        {
          icon: Calendar,
          title: "iCal Sync",
          description: "Import and export reservations via iCal feeds from Airbnb, Booking.com, VRBO, and Google Vacation Rentals.",
          configured: true,
          provider: "Built-in",
        },
        {
          icon: ExternalLink,
          title: "Channel API",
          description: "Direct API connections to OTAs for real-time availability, pricing, and reservation sync.",
          configured: false,
          badge: "Coming Soon",
        },
      ],
    },
    {
      category: "Access & Security",
      items: [
        {
          icon: Lock,
          title: "Smart Locks",
          description: "Generate and manage access codes for smart locks. Supports Yale, August, Schlage, and TTLock.",
          configured: false,
          badge: "Ready",
        },
        {
          icon: Shield,
          title: "ID Verification",
          description: "Verify guest identities with document scanning and facial recognition.",
          configured: false,
          badge: "Coming Soon",
        },
      ],
    },
    {
      category: "Automation & Webhooks",
      items: [
        {
          icon: Zap,
          title: "Automation Engine",
          description: "13 trigger types and 13 action types with template variables, multi-action chains, and run history.",
          configured: true,
          provider: "Built-in",
        },
        {
          icon: Webhook,
          title: "Webhooks",
          description: "Receive incoming webhooks to trigger automations, and send outgoing webhooks to external services.",
          configured: true,
          provider: "Built-in",
        },
      ],
    },
  ];

  return (
    <AdminLayout>
      <div className="space-y-6 max-w-4xl">
        <div>
          <h1 className="text-2xl font-display font-bold" data-testid="text-page-title">Integrations</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Connect external services to enhance your property management operations.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-green-600" data-testid="text-active-integrations">
                {integrations.reduce((sum, cat) => sum + cat.items.filter(i => i.configured).length, 0)}
              </p>
              <p className="text-xs text-muted-foreground">Active</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold" data-testid="text-available-integrations">
                {integrations.reduce((sum, cat) => sum + cat.items.length, 0)}
              </p>
              <p className="text-xs text-muted-foreground">Available</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-muted-foreground" data-testid="text-coming-soon">
                {integrations.reduce((sum, cat) => sum + cat.items.filter(i => i.badge === 'Coming Soon').length, 0)}
              </p>
              <p className="text-xs text-muted-foreground">Coming Soon</p>
            </CardContent>
          </Card>
        </div>

        {integrations.map((category) => (
          <div key={category.category}>
            <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3" data-testid={`text-category-${category.category.toLowerCase()}`}>
              {category.category}
            </h2>
            <div className="space-y-3">
              {category.items.map((item) => (
                <IntegrationCard key={item.title} {...item} />
              ))}
            </div>
          </div>
        ))}


        <div>
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
            Calendar Sync Schedule
          </h2>
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-primary" />
                <CardTitle className="text-base">Auto-Sync Times</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Set the daily times when Staio will automatically sync all apartments that have an iCal URL configured.
              </p>
              <div className="flex flex-wrap gap-3">
                <Button
                  variant="outline"
                  onClick={() => { setSyncAllResult(null); syncAllNow.mutate(); }}
                  disabled={syncAllNow.isPending || backfillJobs.isPending}
                  data-testid="button-sync-all-now"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${syncAllNow.isPending ? 'animate-spin' : ''}`} />
                  {syncAllNow.isPending ? "Syncing..." : "Sync All iCal Now"}
                </Button>
                <Button
                  variant="default"
                  onClick={() => { setBackfillResult(null); backfillJobs.mutate(); }}
                  disabled={backfillJobs.isPending || syncAllNow.isPending}
                  data-testid="button-backfill-cleaning-jobs"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${backfillJobs.isPending ? 'animate-spin' : ''}`} />
                  {backfillJobs.isPending ? "Creating missing jobs..." : "Create Missing Cleaning Jobs"}
                </Button>
              </div>
              {syncAllResult && (
                <div className="rounded-md border bg-muted/40 p-3 text-sm space-y-1">
                  <p className="font-medium">iCal sync complete — {syncAllResult.synced} apartments</p>
                  <p className="text-muted-foreground">+{syncAllResult.created} new reservations · ~{syncAllResult.updated} updated · {syncAllResult.cancelled} cancelled</p>
                  {syncAllResult.errors.length > 0 && (
                    <p className="text-destructive text-xs">{syncAllResult.errors.length} error(s): {syncAllResult.errors.slice(0, 3).join(' · ')}{syncAllResult.errors.length > 3 ? ` +${syncAllResult.errors.length - 3} more` : ''}</p>
                  )}
                </div>
              )}
              {backfillResult && (
                <div className="rounded-md border bg-green-50 dark:bg-green-900/20 border-green-200 p-3 text-sm space-y-1">
                  <p className="font-medium text-green-800 dark:text-green-200">Backfill complete</p>
                  <p className="text-green-700 dark:text-green-300">{backfillResult.created} cleaning jobs created · {backfillResult.skipped} already existed{backfillResult.errors > 0 ? ` · ${backfillResult.errors} errors` : ''}</p>
                </div>
              )}
              <div className="flex flex-wrap gap-4 items-end">
                <div className="space-y-1.5">
                  <Label htmlFor="sync-time-1">First sync</Label>
                  <Input
                    id="sync-time-1"
                    type="time"
                    value={syncTime1}
                    onChange={e => setSyncTime1(e.target.value)}
                    className="w-36"
                    data-testid="input-sync-time-1"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="sync-time-2">Second sync</Label>
                  <Input
                    id="sync-time-2"
                    type="time"
                    value={syncTime2}
                    onChange={e => setSyncTime2(e.target.value)}
                    className="w-36"
                    data-testid="input-sync-time-2"
                  />
                </div>
                <Button
                  onClick={() => saveSyncTimes.mutate()}
                  disabled={saveSyncTimes.isPending}
                  data-testid="button-save-sync-times"
                >
                  {syncScheduleSaved ? "Saved!" : saveSyncTimes.isPending ? "Saving..." : "Save Schedule"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Email Integration</DialogTitle>
              <DialogDescription>
                Configure your email service for transactional emails.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              {emailStatus?.configured ? (
                <>
                  <div className="rounded-lg border border-green-200 bg-green-50 dark:bg-green-900/20 dark:border-green-800 p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                      <span className="font-medium text-green-800 dark:text-green-200">Connected to Resend</span>
                    </div>
                    <p className="text-sm text-green-700 dark:text-green-300">
                      From: {emailStatus.from}
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label>Send Test Email</Label>
                    <div className="flex gap-2">
                      <Input
                        type="email"
                        placeholder="test@example.com"
                        value={testEmail}
                        onChange={(e) => setTestEmail(e.target.value)}
                        data-testid="input-test-email"
                      />
                      <Button
                        onClick={() => sendTestEmail.mutate(testEmail)}
                        disabled={!testEmail || sendTestEmail.isPending}
                        data-testid="button-send-test-email"
                      >
                        <Send className="w-4 h-4 mr-1" />
                        {sendTestEmail.isPending ? "Sending..." : "Send"}
                      </Button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="space-y-4">
                  <div className="rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800 p-4">
                    <p className="text-sm text-amber-800 dark:text-amber-200">
                      To enable email functionality, you need to set up the Resend integration. This will allow Staio to send booking confirmations, check-in instructions, payment receipts, and automation emails.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <h4 className="font-medium text-sm">Setup Steps:</h4>
                    <ol className="list-decimal list-inside text-sm text-muted-foreground space-y-1.5">
                      <li>Create a free account at <a href="https://resend.com" target="_blank" rel="noopener noreferrer" className="text-primary underline">resend.com</a></li>
                      <li>Add and verify your domain in Resend</li>
                      <li>Create an API key in your Resend dashboard</li>
                      <li>Add RESEND_API_KEY as a secret in your project settings</li>
                      <li>Optionally set EMAIL_FROM to customize the sender address</li>
                    </ol>
                  </div>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
